/**
 * La única puerta de importación desde el catálogo de un proveedor.
 *
 * ⭐ VIVE EN SU PROPIO ARCHIVO PORQUE TIENE TRES DUEÑOS: Aliclik, Dropi y Smarty Drop importan por
 *    acá, y ninguno de los tres es más dueño que los otros. Estaba escondida como función privada
 *    dentro de `ordersApi`, así que el catálogo de Smarty Drop —que vive en `src/lib/dropshipping/`—
 *    no podía mudarse sin arrastrar consigo la mitad de `ordersApi` o duplicar la puerta. Sacarla
 *    acá es lo que permitió mover la capa de datos de Drop a su módulo.
 *
 * 🪤 Y SIGUE SIENDO UNA SOLA. La tentación al separar era que cada catálogo se llevara su copia. El
 *    comentario de adentro explica por qué eso rompe cosas: la invalidación de la lista de
 *    Productos se hace ACÁ, una vez, y así el próximo proveedor la hereda sin acordarse.
 */
import { supabase } from '@/lib/supabase';

/** Lo que devuelve la puerta de importación, además del id. */
export interface SupplierImportResult {
  productId: string;
  /** `true` si ese SKU ya estaba importado y se reutilizó el producto. */
  already?: boolean;
  /**
   * `true` si la foto quedó en cola. La descarga NO ocurre en esta llamada: la
   * hace `media-ingest-worker` en segundos. Antes se bajaba acá y el comerciante
   * esperaba ~2 s por producto contra un host ajeno.
   */
  imagenPendiente?: boolean;
  /**
   * `false` mientras se espera la foto: un producto sin imagen no se le muestra
   * a un cliente. El worker lo publica al traerla, si sirve.
   */
  publicado?: boolean;
}

/**
 * Única puerta de importación desde el catálogo de un proveedor.
 *
 * Todo el trabajo vive en la edge function `import-supplier-product`: dedupe,
 * ingesta de la foto a R2, alta del producto y su mapeo. Acá NO se inserta
 * nada.
 *
 * Es a propósito. Antes cada catálogo insertaba en `products` desde el
 * navegador guardando la URL del proveedor, y esa foto no era nuestra: si el
 * proveedor la borraba, la tienda quedaba con la imagen rota. Además el
 * navegador no puede descargar del host del proveedor sin que ese host mande
 * CORS —ImgBB no lo promete—, así que la copia solo puede hacerse del lado del
 * servidor. Ver supabase/functions/import-supplier-product/index.ts.
 */
export async function importFromSupplier(
  tenantId: string,
  supplier: 'aliclik' | 'dropi' | 'smartydrop',
  item: unknown,
): Promise<SupplierImportResult> {
  const { data, error } = await supabase.functions.invoke('import-supplier-product', {
    body: { tenant_id: tenantId, supplier, item },
  });
  const res = (data ?? {}) as {
    ok?: boolean; error?: string; detail?: string; product_id?: string; already?: boolean;
    publicado?: boolean; imagen_pendiente?: boolean;
  };
  const extra = res.detail ? ` · ${res.detail}` : '';
  if (error) throw new Error((res.error || error.message || 'No se pudo importar el producto.') + extra);
  if (!res.ok || !res.product_id) throw new Error((res.error || 'No se pudo importar el producto.') + extra);

  /*
   * 🪤 La lista de Productos vive en la caché del panel, y acaba de aparecer un producto nuevo que
   * ella no vio nacer. Sin esto, el comerciante importa, va a Productos y NO ESTÁ — tiene que
   * recargar la página para verlo. Reportado el 22-ago-2026 probando Smarty Drop.
   *
   * Se invalida acá, en la ÚNICA puerta de importación, y no en cada catálogo: así vale para Dropi,
   * Aliclik y Smarty Drop por igual, y el próximo proveedor lo hereda sin acordarse.
   */
  const { queryClient } = await import('@/lib/queryClient');
  await queryClient.invalidateQueries({ queryKey: ['products'] });

  return {
    productId: res.product_id,
    already: res.already,
    imagenPendiente: res.imagen_pendiente,
    publicado: res.publicado,
  };
}
